import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { HeaderHelperService } from '../header-helper.service';
import { NotificationTemplateModel } from '../../models/notification/notification-template.model';
import { AiImagesModel } from '../../models/consultation/ai-images.model';
import { PatientCaseModel } from '../../models/consultation/patient-case.model';
import { AssignDoctorToPatientRecordModel } from '../../models/consultation/assign-doctor-to-case.model';
import { ConclusionOptionInfoModel } from '../../models/consultation/conclusion-option-info.model';
import { FilterModel } from '../../models/filters/filters.model';
import { SortModel } from '../../models/filters/sort.model';
import { SkinAnalysisMetricsModel } from '../../models/consultation/skin-analysis-metrics.model';

@Injectable({
  providedIn: 'root',
})
export class ConsultationsService {
  constructor(
    private http: HttpClient,
    private headerHelperService: HeaderHelperService
  ) { }

  async patientRecord(
    successCallBack?: () => void,
    errorCallBack?: (message: HttpErrorResponse) => void
  ): Promise<any> {
    let url = `${environment.apiLocations.patient
      }/PatientRecord?PageRequest.PageIndex=${1}&PageRequest.PageSize=${10}`;
    const observable: Observable<any> = this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });

    const promiseData = firstValueFrom(observable);
    promiseData
      .then((value) => successCallBack())
      .catch((error) => errorCallBack(error));
    return await promiseData;
  }

  consultationList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${environment.apiLocations.patient
      }/PatientRecord?PageRequest.PageIndex=${page - 1
      }&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${sort.orderBy == 'descending'
        }`;
    }

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  referralList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${environment.apiLocations.patient
      }/PatientRecord/GetReferralRequiredPatients?PageRequest.PageIndex=${page - 1
      }&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${sort.orderBy == 'descending'
        }`;
    }

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  medicalInformations(): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetMedicalInformations`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  saveAndTakeImage(consultationData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecord`,
      consultationData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  consultationDetailByPatientRecordCaseId(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationDetail/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordImagesByPatientId(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseImages/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordCaseImageFile(patientRecordCaseImageId: string): Observable<Blob> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseImageFile/${patientRecordCaseImageId}`;

    return this.http.get(url, {
      headers: this.headerHelperService.getHeaders(),
      responseType: 'blob',
    });
  }

  patientRecordCaseImagesByPatientRecordCaseId(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationDetail/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordCaseReportsByPatientRecordCaseId(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRapport/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineList(): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineList`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineByCaseId(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/GetConsultationTimeLineByCaseId/GetPatientRapport/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  conclusionOptions(): Observable<any> {
    let url = `${environment.apiLocations.patient}/ConclusionOption`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  sendReport(reportData: any, patientRecordId: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecordCase/CreateConsultationReport/${patientRecordId}`,
      reportData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  previewReport(reportData: any, patientRecordId: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecordCase/SaveAndPreviewConsultationReport/${patientRecordId}`,
      reportData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  getReport(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationRapport/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getReportWithPatientRecordId(patientRecordId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecord/GetPreviewConsultationReport/${patientRecordId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  checkConsultationReport(patientRecordId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecord/CheckPreviewConsultationReport/${patientRecordId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  async getConsultationReportTemplate(
    successCallBack?: () => void,
    errorCallBack?: (message: HttpErrorResponse) => void
  ): Promise<NotificationTemplateModel> {
    let url = `${environment.apiLocations.notification}/NotificationTemplate/GetConsultationReportTemplate`;

    const observable: Observable<NotificationTemplateModel> =
      this.http.get<NotificationTemplateModel>(url, {
        headers: this.headerHelperService.getHeaders(),
      });

    const promiseData = firstValueFrom(observable);
    promiseData.then((value) => successCallBack()).catch((error) => { });
    return await promiseData;
  }

  getConsultationReportTemplateById(
    id: string,
    mailBodyData: any
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/GetConclusionReportTemplate/GetConsultationReportTemplateWithInformation/${id}`;

    return this.http.post<any>(url, JSON.stringify(mailBodyData), {
      headers: this.headerHelperService
        .getHeaders()
        .append('Content-Type', 'application/json'),
    });
  }

  async getAiImages(
    id: string,
    successCallBack?: () => void,
    errorCallBack?: (message: HttpErrorResponse) => void
  ): Promise<AiImagesModel> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetAiImages/${id}`;

    const observable: Observable<AiImagesModel> = this.http.get<AiImagesModel>(
      url,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );

    const promiseData = firstValueFrom(observable);
    promiseData
      .then((value) => successCallBack())
      .catch((error) => errorCallBack(error));
    return await promiseData;
  }

  async getCasesByRecordId(id: string): Promise<PatientCaseModel[]> {
    let url = `${environment.apiLocations.patient}/PatientRecord/GetCasesByRecordId/${id}`;

    const observable: Observable<PatientCaseModel[]> = this.http.get<
      PatientCaseModel[]
    >(url, {
      headers: this.headerHelperService.getHeaders(),
    });

    const promiseData = firstValueFrom(observable);
    return await promiseData;
  }

  async completePatientRecordCase(
    patientRecordCaseId: string,
    conclusionOptionInfos: ConclusionOptionInfoModel[],
    diagnoseTypeContentId: string,
    successCallBack?: () => void,
    errorCallBack?: (message: HttpErrorResponse) => void
  ): Promise<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/CompletePatientRecordCase/${patientRecordCaseId}`;

    const observable: Observable<any> = this.http.put<any>(
      url,
      {
        patientRecordCaseId,
        conclusionOptionInfos,
        diagnoseTypeContentId,
      },
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );

    const promiseData = firstValueFrom(observable);
    promiseData
      .then((value) => successCallBack())
      .catch((error) => errorCallBack(error));
    return await promiseData;
  }

  assignDoctorToPatientRecord(
    patientRecordId: string
  ): Observable<AssignDoctorToPatientRecordModel> {
    let url = `${environment.apiLocations.patient}/PatientRecord/AssignDoctorToPatientRecord/${patientRecordId}`;

    return this.http.put<AssignDoctorToPatientRecordModel>(
      url,
      {},
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  removeDoctorFromPatientRecord(patientRecordId: string): Observable<void> {
    let url = `${environment.apiLocations.patient}/PatientRecord/RemoveDoctorFromPatientRecord/${patientRecordId}`;

    return this.http.delete<void>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  annotationList(): Observable<any> {
    let url = `${environment.apiLocations.patient}/Annotation`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  rectanglesWithAnnotations(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/CaseImageAnnotation/GetCaseImageAnnotationsByPatientRecordCaseImageId/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  caseImageAnnotation(data: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/CaseImageAnnotation`,
      data,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  diagnoseTypeCategoryList(): Observable<any> {
    let url = `${environment.apiLocations.patient}/DiagnoseType/DiagnoseTypeCategoryList`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  diagnoseTypeContentListByCategory(diagnoseTypeId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/DiagnoseType/DiagnoseTypeContentListByCategory/${diagnoseTypeId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  conclusions(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConclusions/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  deleteConsultation(patientRecordId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecord/${patientRecordId}`;

    return this.http.delete<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getSkinAnalysisMetrics(patientRecordCaseId: string): Observable<SkinAnalysisMetricsModel | null> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetSkinAnalysisMetrics/${patientRecordCaseId}`;

    return this.http.get<SkinAnalysisMetricsModel | null>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }
}
